import { Button } from '@shipt/design-system-buttons'
import { Column, Row, Stack } from '@shipt/design-system-layouts'
import { Body, Headline } from '@shipt/design-system-typography'
import { FeedbackBanner } from '@shipt/design-system-banner'
import { Skeleton } from '@shipt/design-system-loading'
import styled from 'styled-components'
import { useRadio } from '@/features/shared/hooks/a11y/useRadio'
import { screenSizes } from '@shipt/design-system-themes'
import { useWatch, type UseFormReturn } from 'react-hook-form'
import { type GiftCardFormInput } from '@/features/cms/components/CMS/components/GiftCardPurchaseForm/GiftCardRecipientForm'
import { CurrencyInput } from '@shipt/design-system-inputs'
import {
  getMembershipLabel,
  isInvalidGiftCardValue,
  isKnownMembershipPlan,
} from '@/features/cms/components/CMS/components/GiftCardPurchaseForm/utils'
import { formatCents } from '@/features/shared/utils/formatter'
import { useQueryGiftCardChoices } from '@/features/purchase/services/GiftCard/queries'
import { theme } from '@/features/shared/constants/shiptTheme'

export const GiftCardChoices = ({
  giftCardForm,
}: {
  giftCardForm: UseFormReturn<GiftCardFormInput, object>
}) => {
  const { data: membershipOptions, isLoading } = useQueryGiftCardChoices()

  // Hooks must be called before any early returns
  const { setValue, control } = giftCardForm
  const watchCardChoice = useWatch({ control, name: 'cardChoice' })

  const invalidEntry = isInvalidGiftCardValue(watchCardChoice)
    ? 'Please enter a value between $25 and $250'
    : ''

  const valueOptions = ['25', '50', '100', '250', 'Custom']

  const giftMembershipOptions =
    membershipOptions?.gift_membership_options.filter((opt) => isKnownMembershipPlan(opt.value)) ??
    []

  const membershipValues = giftMembershipOptions.map((opt) => opt.value)

  const optionIndex = [...valueOptions, ...membershipValues]

  const onGiftChange = (value: string) => {
    setValue('cardChoice', value)
  }

  const { getRadioGroupProps, getRadioBtnProps, selectedIndex } = useRadio<
    string,
    string,
    HTMLButtonElement
  >({
    onChange: onGiftChange,
    optionValues: optionIndex,
    value: watchCardChoice,
    getValue: (option: string) => option,
  })

  if (isLoading) {
    return <Skeleton width="100%" height={460} />
  }

  const isCustomEntry = watchCardChoice === 'Custom' || !optionIndex.includes(watchCardChoice)

  const renderMembershipChips = () => {
    if (giftMembershipOptions.length === 0) {
      return (
        <FeedbackBanner
          variant="negative"
          title="Something went wrong"
          description="Membership gift cards are unavailable right now. Please try again later."
          hasIcon
        />
      )
    }

    return giftMembershipOptions.map((option, index) => {
      const label = getMembershipLabel(option.value)
      const activePrice = formatCents(option.price_cents)
      const normalPrice = formatCents(option.original_price_cents)
      return (
        <ChipButton
          key={option.value}
          as={Button}
          type="button"
          variant="secondary"
          size="sm"
          isSelected={option.value === optionIndex[selectedIndex]}
          {...getRadioBtnProps(valueOptions.length + index)}
          name="GiftCardChoice"
          aria-label={`${label} ${activePrice}`}
        >
          {option.is_promo ? (
            <>
              {label} | <OriginalPrice>{normalPrice}</OriginalPrice> <span>{activePrice}</span>
            </>
          ) : (
            <>
              {label} | {activePrice}
            </>
          )}
        </ChipButton>
      )
    })
  }

  return (
    <>
      <Headline as="h2" size="md" align="center">
        Choose gift
      </Headline>
      <RadioWrapper
        direction={{ base: 'column', md: 'row' }}
        spacing="xl"
        {...getRadioGroupProps()}
      >
        <Column
          align={{ base: 'start', md: 'center' }}
          padding={{ base: [0], md: ['sm', 0, 0, 0] }}
          spacing="md"
        >
          <GiftOptionHeading>
            <Body className="gift-option-text">Gift Card*</Body>
            <Body className="gift-option-text" variant="secondary">
              No membership necessary.
            </Body>
            <Body className="gift-option-text" variant="secondary">
              Gift card value never expires.
            </Body>
          </GiftOptionHeading>
          <Row spacing="sm" wrap justify={{ base: 'start', md: 'center' }}>
            {valueOptions.map((option, i) => {
              const isSelected = selectedIndex === i || (isCustomEntry && option === 'Custom')
              return (
                <ChipButton
                  as={Button}
                  type="button"
                  key={option}
                  variant="secondary"
                  size="sm"
                  isSelected={isSelected}
                  name="GiftCardChoice"
                  {...getRadioBtnProps(i)}
                >
                  {option !== 'Custom' && '$'}
                  {option}
                </ChipButton>
              )
            })}
          </Row>
          {!!isCustomEntry && (
            <CurrencyInput
              aria-label="Custom Gift Card Amount"
              name="Custom Gift Card Amount"
              onChange={(e) => setValue('cardChoice', e.target.value)}
              size="md"
              errorMessage={invalidEntry}
              shouldPreventDecimal
              placeholder="$0"
              prefix="$"
            />
          )}
        </Column>
        <Divider direction={{ base: 'row', md: 'column' }} align="center">
          <TopVertLine />
          <Body strong size="xl">
            or
          </Body>
          <BottomVertLine />
        </Divider>
        <Column
          align={{ base: 'start', md: 'center' }}
          padding={{ base: [0], md: ['sm', 0, 0, 0] }}
          spacing="md"
        >
          <GiftOptionHeading>
            <Body className="gift-option-text">Shipt Membership Gift Card**</Body>
            <Body className="gift-option-text" variant="secondary">
              Gift memberships may be redeemed by new subscribers only.
            </Body>
          </GiftOptionHeading>
          <Row spacing="md" wrap justify={{ base: 'start', md: 'center' }}>
            {renderMembershipChips()}
          </Row>
        </Column>
      </RadioWrapper>
      <div>
        <Body align="center" variant="secondary" size="sm">
          *Gift cards cannot be used to purchase alcohol items. If an order includes both alcohol
          and non-alcohol items, the gift card will be applied to the non-alcoholic items only. Gift
          cards cannot be applied to tips or service fees.
        </Body>
        <Body align="center" variant="secondary" size="sm">
          **A Shipt membership gift card can only be used to redeem a Shipt membership. It cannot be
          used at checkout for items, tips, or taxes, or to cover fees.
        </Body>
      </div>
    </>
  )
}

const RadioWrapper = styled(Stack)`
  align-self: stretch;
`
const GiftOptionHeading = styled(Column)`
  .gift-option-text {
    text-align: left;
  }

  @media ${screenSizes.tablet} {
    .gift-option-text {
      text-align: center;
    }
  }
`
const Divider = styled(Stack)`
  align-self: stretch;
  gap: 0.5rem;

  @media ${screenSizes.tablet} {
    gap: 0;
  }
`

const TopVertLine = styled.div`
  background-color: ${theme.color.border.base.neutral.subtle};
  width: 18%;
  height: 1px;

  @media ${screenSizes.tablet} {
    width: 1px;
    height: 18%;
  }
`
const BottomVertLine = styled.div`
  background-color: ${theme.color.border.base.neutral.subtle};
  width: 82%;
  height: 1px;

  @media ${screenSizes.tablet} {
    width: 1px;
    height: 82%;
  }
`

const OriginalPrice = styled.span`
  color: ${theme.color.content.base.neutral.secondary};
  text-decoration: line-through;
  font-weight: 400;
`

const ChipButton = styled.div<{ isSelected: boolean }>`
  gap: 0.4rem;
  border-color: ${({ isSelected }) =>
    isSelected
      ? theme.color.border.action.primary_heavy_active
      : theme.color.border.action.primary_heavy};
  background-color: ${({ isSelected }) => (isSelected ? theme.color.background.action.primary_soft_active : 'transparent')};
  color: ${({ isSelected }) =>
    isSelected
      ? theme.color.content.action.secondary_active
      : theme.color.content.action.secondary};

  ${OriginalPrice} {
    transition: inherit;
  }

  &:hover {
    ${OriginalPrice} {
      color: inherit;
    }
  }
`
